import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

const CartContext = createContext(null);

const STORAGE_KEY = 'tt:cart:v1';

const readStore = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

const writeStore = (items) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch { /* ignore quota errors */ }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => readStore());
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState(null);

  // Keep multiple tabs in sync
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setItems(readStore());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  useEffect(() => {
    writeStore(items);
  }, [items]);

  const addItem = useCallback((product, size = null, qty = 1) => {
    setError(null);
    const resolvedSize = size || product.sizes?.[0] || 'Default';
    const price = product.sizePrices?.[resolvedSize] ?? product.price ?? 0;
    const lineKey = `${product.handle || product.id}-${resolvedSize}`;
    const image =
      product.media?.primary_image ||
      product.images?.[0] ||
      null;

    setItems((prev) => {
      const existing = prev.find((i) => i.key === lineKey);
      if (existing) {
        return prev.map((i) =>
          i.key === lineKey ? { ...i, qty: i.qty + qty } : i
        );
      }
      return [
        ...prev,
        {
          key: lineKey,
          lineId: lineKey,
          id: product.handle || product.id,
          name: product.name,
          price,
          currency: product.currency || 'AUD',
          image,
          size: resolvedSize,
          qty,
        },
      ];
    });
    setIsOpen(true);
  }, []);

  const updateQty = useCallback((lineId, quantity) => {
    const safeQty = Math.max(1, quantity);
    setItems((prev) =>
      prev.map((i) => (i.lineId === lineId ? { ...i, qty: safeQty } : i))
    );
  }, []);

  const removeItem = useCallback((lineId) => {
    setItems((prev) => prev.filter((i) => i.lineId !== lineId));
  }, []);

  const clear = useCallback(() => {
    setItems([]);
  }, []);

  const count = useMemo(() => items.reduce((sum, i) => sum + i.qty, 0), [items]);
  const subtotal = useMemo(
    () => items.reduce((sum, i) => sum + i.price * i.qty, 0),
    [items]
  );
  const currency = items[0]?.currency || 'AUD';

  return (
    <CartContext.Provider
      value={{
        items,
        count,
        subtotal,
        currency,
        checkoutUrl: null,
        loading: false,
        error,
        addItem,
        updateQty,
        removeItem,
        clear,
        isOpen,
        setIsOpen,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
};
